import { LogManager } from '@src/utility/Logger';
import { speakerNameLine } from '@src/utility/speakerRole';

import type { Speaker } from '../components/content-components/SpeakerTypes';
import type { DatoClientStore } from '@src/stores-v2/DatoClientStore';

const logger = LogManager.getLogger('utility:speakerUtils');

// ============================================================================
// TYPES
// ============================================================================

export interface DatoSpeaker {
  id: string;
  name?: string;
  firstName?: string | null;
  lastName?: string | null;
  title?: string;
  role?: string | null;
  organization?: string | null;
  voice?: string;
  bio?: string;
  headshots?: Array<{ id: string; url: string }>;
}

export interface SpeakerVoiceMapping {
  mappings: Record<string, string>; // Maps speakerLabel to Dato speaker ID
  storyId: string;
  lastUpdated: number;
}

// ============================================================================
// CACHING
// ============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Cache for speaker voice mappings (keyed by storyId)
const speakerMappingsCache = new Map<
  string,
  { data: SpeakerVoiceMapping | null; timestamp: number }
>();

// Cache for Dato speakers (keyed by storyId)
const datoSpeakersCache = new Map<
  string,
  { data: DatoSpeaker[]; timestamp: number }
>();

/**
 * Clears all speaker-related caches
 * Call this when speaker data is modified
 */
export function clearSpeakerCache(): void {
  speakerMappingsCache.clear();
  datoSpeakersCache.clear();
  logger.debug('Speaker caches cleared');
}

/**
 * Extracts unique speakers from transcript elements
 *
 * @param elements - The transcript elements to process
 * @returns Array of unique speaker labels
 */
export function extractSpeakerLabelsFromTranscriptElements(
  elements: any[],
): string[] {
  // Set to track unique speaker labels
  const speakerLabels = new Set<string>();

  // Process each element to find speaker labels
  elements.forEach(element => {
    if (element.speaker_label && element.speaker_label.trim() !== '') {
      speakerLabels.add(element.speaker_label);
    }
  });

  // Convert the set to an array
  return Array.from(speakerLabels);
}

/**
 * Extracts speaker name from quote with attribution format
 *
 * @param quote - The quote text that might contain attribution
 * @returns The extracted speaker name or null if not found
 */
export function extractSpeakerFromQuote(quote: string): string | null {
  if (!quote || typeof quote !== 'string') return null;

  // Match pattern: any text followed by " --- " and then the speaker name
  const attributionRegex = /^(.*?)(?:\s+---\s+)(.+)$/;
  const match = quote.trim().match(attributionRegex);

  if (match && match[2]) {
    // Trim any extra quote characters that might be present in the speaker name
    return match[2].trim().replace(/["']/g, '');
  }

  return null;
}

/**
 * Cleans quote text by removing attribution if present
 *
 * @param quote - The quote text that might contain attribution
 * @returns The cleaned quote text
 */
export function cleanQuoteText(quote: string): string {
  if (!quote || typeof quote !== 'string') return quote;

  // Match pattern: any text followed by " --- " and then the speaker name
  const attributionRegex = /^(.*?)(?:\s+---\s+)(.+)$/;
  const match = quote.trim().match(attributionRegex);

  if (match && match[1]) {
    return match[1].trim();
  }

  return quote.trim();
}

/**
 * Combines speaker data from transcript elements, mappings and Dato speakers
 *
 * @param speakerLabels - The unique speaker labels from transcript
 * @param speakerMappings - Mappings from speaker labels to Dato speaker IDs
 * @param datoSpeakers - Speaker information from Dato CMS
 * @returns Array of complete speaker objects
 */
export function buildSpeakersFromMappings(
  speakerLabels: string[],
  speakerMappings: SpeakerVoiceMapping | null,
  datoSpeakers: DatoSpeaker[],
): Speaker[] {
  // Create a map of Dato speaker IDs to speaker information
  const datoSpeakerMap = datoSpeakers.reduce(
    (map, speaker) => {
      map[speaker.id] = speaker;
      return map;
    },
    {} as Record<string, DatoSpeaker>,
  );

  // Group speaker labels by their resolved Dato speaker ID to deduplicate
  // (e.g., after combining spk_0 and spk_3 as the same person)
  const datoIdToLabels = new Map<string, string[]>();

  for (const speakerLabel of speakerLabels) {
    let datoSpeakerId: string | undefined;
    if (speakerLabel.startsWith('override_')) {
      // Override labels encode the Dato speaker ID in the key itself.
      // The mapping value is undo metadata (previous speaker), not the assignee.
      datoSpeakerId = speakerLabel.replace('override_', '');
    } else {
      datoSpeakerId = speakerMappings?.mappings?.[speakerLabel];
    }
    if (datoSpeakerId && datoSpeakerMap[datoSpeakerId]) {
      const existing = datoIdToLabels.get(datoSpeakerId);
      if (existing) {
        existing.push(speakerLabel);
      } else {
        datoIdToLabels.set(datoSpeakerId, [speakerLabel]);
      }
    }
  }

  // Build one Speaker per unique Dato speaker (skip anonymous/unnamed)
  const speakers: Speaker[] = [];
  for (const [datoSpeakerId, labels] of datoIdToLabels) {
    const datoSpeaker = datoSpeakerMap[datoSpeakerId];
    // A speaker with only First / Last Name still has a Name line (PLAT-12176).
    if (!datoSpeaker || !speakerNameLine(datoSpeaker)) continue;

    speakers.push({
      id: datoSpeaker.id,
      speakerLabel: labels[0],
      allSpeakerLabels: labels.length > 1 ? labels : undefined,
      name: speakerNameLine(datoSpeaker) || null,
      firstName: datoSpeaker.firstName || null,
      lastName: datoSpeaker.lastName || null,
      title: datoSpeaker.title || null,
      role: datoSpeaker.role || null,
      organization: datoSpeaker.organization || null,
      voice: datoSpeaker.voice || null,
      bio: datoSpeaker.bio || null,
      headshots: datoSpeaker.headshots?.map(h => h.url) || [],
      isAnonymous: false,
    });
  }

  return speakers;
}

/**
 * Filters transcript elements to include only those from selected speakers
 *
 * @param elements - The transcript elements to filter
 * @param selectedSpeakerLabels - Array of speaker labels to include
 * @returns Filtered transcript elements
 */
export function filterElementsBySpeakers(
  elements: any[],
  selectedSpeakerLabels: string[],
): any[] {
  // If no speakers selected or empty selection, return all elements
  if (!selectedSpeakerLabels || selectedSpeakerLabels.length === 0) {
    return elements;
  }

  let includeFollowingPunct = false;

  // Filter elements to include only selected speakers; punctuation follows the last included speaker
  return elements.filter(element => {
    const isPunct = element.type === 'punct';
    const matchesSpeaker =
      element.speaker_label &&
      selectedSpeakerLabels.includes(element.speaker_label);

    if (matchesSpeaker) {
      includeFollowingPunct = true;
      return true;
    }

    if (isPunct) {
      return includeFollowingPunct;
    }

    // Non-punct, non-matching element resets punct inclusion
    includeFollowingPunct = false;
    return false;
  });
}

/**
 * Formats transcript with speaker information for better prompts
 *
 * @param elements - The transcript elements to format
 * @param speakers - Information about speakers
 * @returns Formatted transcript text
 */
export function formatTranscriptWithSpeakers(
  elements: any[],
  speakers: Speaker[],
): string {
  if (!elements || elements.length === 0) {
    return '';
  }

  // Create a speaker map for easy lookup (index by all consolidated labels)
  const speakerMap = speakers.reduce(
    (map, speaker) => {
      const labels = speaker.allSpeakerLabels ?? [speaker.speakerLabel];
      for (const label of labels) {
        map[label] = speaker;
      }
      return map;
    },
    {} as Record<string, Speaker>,
  );

  // Group elements by speaker
  const speakerGroups: Record<string, string[]> = {};

  elements.forEach(element => {
    const speakerLabel = element.speaker_label || 'unknown';
    if (!speakerGroups[speakerLabel]) {
      speakerGroups[speakerLabel] = [];
    }

    if (element.value) {
      speakerGroups[speakerLabel].push(element.value);
    }
  });

  // Format each speaker's text
  const formattedSpeakers = Object.entries(speakerGroups).map(
    ([speakerLabel, texts]) => {
      const speaker = speakerMap[speakerLabel] || {
        speakerLabel,
        isAnonymous: true,
        name: null,
      };

      let speakerPrefix = '';
      if (speaker.isAnonymous) {
        speakerPrefix = `Speaker ${speaker.speakerLabel}: `;
      } else if (speaker.name && speaker.title) {
        speakerPrefix = `${speaker.name} (${speaker.title}): `;
      } else if (speaker.name) {
        speakerPrefix = `${speaker.name}: `;
      }

      return `${speakerPrefix}${texts.join(' ')}`;
    },
  );

  return formattedSpeakers.join('\n\n');
}

/**
 * Creates a transcript text filtered by selected speakers with proper attribution
 *
 * @param elements - The original transcript elements
 * @param selectedSpeakers - Array of selected speakers to filter by
 * @returns Object containing filtered elements and formatted text
 */
export function createSpeakerFilteredTranscript(
  elements: any[],
  selectedSpeakers: Speaker[],
): { elements: any[]; text: string } {
  if (
    !elements ||
    elements.length === 0 ||
    !selectedSpeakers ||
    selectedSpeakers.length === 0
  ) {
    return {
      elements,
      text: elements
        .filter(el => el.value && el.state !== 'removed')
        .map(el => el.value)
        .join(''),
    };
  }

  // Extract selected speaker labels for filtering (include all consolidated labels)
  const selectedSpeakerLabels = selectedSpeakers.flatMap(
    speaker => speaker.allSpeakerLabels ?? [speaker.speakerLabel],
  );

  // Filter elements to include only those from selected speakers
  const filteredElements = filterElementsBySpeakers(
    elements,
    selectedSpeakerLabels,
  );

  // If filtering resulted in empty array, return original elements
  if (filteredElements.length === 0) {
    logger.warn(
      'Speaker filtering resulted in empty transcript, using complete transcript instead',
    );
    return {
      elements,
      text: elements
        .filter(el => el.value && el.state !== 'removed')
        .map(el => el.value)
        .join(''),
    };
  }

  // Create a map of speaker labels to names for adding to the text
  // const speakerNameMap = selectedSpeakers.reduce((map, speaker) => {
  //   map[speaker.speakerLabel] = speaker.name || `Speaker ${speaker.speakerLabel.replace('spk_', '')}`;
  //   return map;
  // }, {} as Record<string, string>);

  // Group elements by speaker to format them properly
  const speakerGroups: Record<string, string[]> = {};
  let lastSpeakerLabel: string | null = null;

  filteredElements.forEach(el => {
    if (el.value && typeof el.value === 'string' && el.state !== 'removed') {
      if (el.speaker_label) {
        lastSpeakerLabel = el.speaker_label;
      }

      // Whitespace carries no speaker_label; attach it to the last seen speaker.
      // Punctuation DOES carry one (the provider labels marks like words, and
      // since PLAT-11670 section reassignment relabels them with their words).
      const speakerLabel = el.speaker_label || lastSpeakerLabel;
      if (!speakerLabel) return; // We don't want "unknown" speakers when filtering by speakers.

      if (!speakerGroups[speakerLabel]) {
        speakerGroups[speakerLabel] = [];
      }
      speakerGroups[speakerLabel].push(el.value);
    }
  });

  // Format each speaker's text with per-speaker normalization to preserve separators
  const formattedSpeakerTexts = Object.entries(speakerGroups).map(
    ([, texts]) => {
      return texts
        .join('')
        .replace(/\s+/g, ' ')
        .replace(/[\s,]+$/, '')
        .trim();
    },
  );

  // Join speaker segments with double line breaks
  const formattedText = formattedSpeakerTexts.join('\n\n');

  logger.debug(
    `Filtered transcript for speakers: ${selectedSpeakerLabels.join(', ')}`,
  );
  logger.debug(
    `Filtered from ${elements.length} to ${filteredElements.length} elements`,
  );

  return {
    elements: filteredElements,
    text: formattedText,
  };
}

/**
 * Checks if transcript elements have speaker data
 *
 * @param elements - The transcript elements to check
 * @returns Boolean indicating if speaker data exists
 */
export function hasSpeakerData(elements: any[]): boolean {
  if (!elements) return false;
  return elements.some(element => element.speaker_label);
}

/**
 * Fetches speaker voice mappings from API
 * Uses caching with 5 minute TTL to reduce API calls
 *
 * @param storyId - ID of the story to fetch mappings for
 * @returns Speaker voice mappings or null if unavailable
 */
export async function fetchSpeakerVoiceMappings(
  storyId: string,
): Promise<SpeakerVoiceMapping | null> {
  // Check cache first
  const cacheKey = storyId;
  const cached = speakerMappingsCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    logger.debug('Using cached speaker mappings', { storyId });
    return cached.data;
  }

  try {
    const url = `${process.env.REACT_APP_API_URL}/api/story/${storyId}/transcript-data?resource=speaker-mappings&storyId=${storyId}`;
    const response = await fetch(url);
    if (!response.ok) {
      logger.error('Failed to fetch speaker mappings');
      return null;
    }

    const data = await response.json();
    const result = data.data || null;

    // Store in cache
    speakerMappingsCache.set(cacheKey, {
      data: result,
      timestamp: Date.now(),
    });

    return result;
  } catch (error) {
    logger.error('Error fetching speaker mappings:', error);
    return null;
  }
}

/**
 * Updates speaker voice mappings via API
 * Clears cache after successful update
 *
 * @param storyId - ID of the story to update mappings for
 * @param mappings - The updated speaker mappings object
 * @returns True if update was successful, false otherwise
 */
export async function updateSpeakerVoiceMappings(
  storyId: string,
  mappings: SpeakerVoiceMapping,
): Promise<boolean> {
  try {
    const url = `${process.env.REACT_APP_API_URL}/api/story/${storyId}/transcript-data?resource=speaker-mappings&storyId=${storyId}`;

    // Update the timestamp
    mappings.lastUpdated = Date.now();

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        data: mappings,
      }),
    });

    if (!response.ok) {
      logger.error('Failed to update speaker mappings:', response.status);
      return false;
    }

    // Clear cache after successful update
    speakerMappingsCache.delete(storyId);
    logger.debug('Speaker mappings cache cleared after update', { storyId });

    return true;
  } catch (error) {
    logger.error('Error updating speaker mappings:', error);
    return false;
  }
}

/**
 * Fetches story speakers from Dato CMS
 * Uses caching with 5 minute TTL to reduce API calls
 *
 * @param storyId - ID of the story to fetch speakers for
 * @returns Array of Dato speakers
 */
export async function fetchDatoSpeakers(
  storyId: string,
  datoClientStore: DatoClientStore,
): Promise<DatoSpeaker[]> {
  try {
    const result =
      await datoClientStore.storyRepository.getStorySpeakers(storyId);
    // getStorySpeakers returns { storySpeakers, albumSpeakers } —
    // merge both lists so all speakers (story + organization) are available
    if (result && !Array.isArray(result)) {
      const { storySpeakers = [], albumSpeakers = [] } = result as {
        storySpeakers: DatoSpeaker[];
        albumSpeakers: DatoSpeaker[];
      };
      const seen = new Set<string>();
      const merged: DatoSpeaker[] = [];
      for (const s of [...storySpeakers, ...albumSpeakers]) {
        if (!seen.has(s.id)) {
          seen.add(s.id);
          merged.push(s);
        }
      }
      return merged;
    }
    return Array.isArray(result) ? result : [];
  } catch (error) {
    logger.error('Error fetching Dato speakers:', error);
    return [];
  }
}

/**
 * Enhances a prompt with speaker context when filtering by speaker
 *
 * @param prompt - The original prompt
 * @param selectedSpeakers - Array of selected speakers
 * @param allSpeakers - Array of all available speakers
 * @returns Enhanced prompt with speaker context
 */
export function enhancePromptWithSpeakerContext(
  prompt: string,
  selectedSpeakers: Speaker[],
  allSpeakers: Speaker[],
): string {
  // If all speakers are selected or empty selection, return original prompt
  if (
    selectedSpeakers.length === allSpeakers.length ||
    selectedSpeakers.length === 0
  ) {
    return prompt;
  }

  // Get names of selected speakers for the prompt
  const speakerNames = selectedSpeakers
    .map(speaker => {
      if (speaker.isAnonymous) {
        return `Speaker ${speaker.speakerLabel}`;
      } else {
        return speaker.name || `Speaker ${speaker.speakerLabel}`;
      }
    })
    .join(', ');

  // Enhance the prompt with speaker context
  return `Based ONLY on the words of ${speakerNames}, ${prompt}`;
}

/**
 * Gets a readable speaker name from a speaker label
 * @param speakerLabel The anonymous speaker label (e.g., "spk_0") or null/undefined
 * @returns A formatted speaker name
 */
export function getSpeakerNameFromLabel(
  speakerLabel: string | null | undefined,
): string {
  if (!speakerLabel) return 'Unknown';

  // Format anonymous speaker labels
  if (speakerLabel.startsWith('spk_')) {
    return `Speaker ${parseInt(speakerLabel.replace('spk_', '')) + 1}`;
  }

  // If it's not an anonymous label, return as is
  return speakerLabel;
}

/**
 * Speaker color palette - matches SpeakerService palette
 */
export const SPEAKER_COLORS = [
  '#4299E1', // Blue
  '#48BB78', // Green
  '#ED8936', // Orange
  '#9F7AEA', // Purple
  '#F56565', // Red
  '#38B2AC', // Teal
  '#ED64A6', // Pink
  '#ECC94B', // Yellow
  '#667EEA', // Indigo
  '#FC8181', // Light Red
  '#4FD1C5', // Light Teal
  '#B794F4', // Light Purple
];

/**
 * Gets a consistent color for a speaker label
 * Uses a hash-based approach to ensure the same speaker always gets the same color
 * @param speakerLabel The anonymous speaker label (e.g., "spk_0") or null/undefined
 * @returns A hex color string
 */
export function getSpeakerColorFromLabel(
  speakerLabel: string | null | undefined,
): string {
  if (!speakerLabel) return '#A9A9A9';
  return (
    SPEAKER_COLORS[hashSpeakerLabel(speakerLabel) % SPEAKER_COLORS.length] ||
    '#A9A9A9'
  );
}

// Hash of the label's last 4 characters (the varying tail of spk_N labels).
function hashSpeakerLabel(label: string): number {
  return label
    .slice(-4)
    .split('')
    .reduce((a: number, b: string) => a + b.charCodeAt(0), 0);
}

/**
 * Deterministic, collision-free avatar colors for a known set of people
 * (e.g. a workspace roster). Two people must never share a color while the
 * palette lasts: each name starts at its hash slot (agreeing with
 * `getSpeakerColorFromLabel` when there's no clash) and probes forward to the
 * next unused color. Names are processed in sorted order, so every surface
 * that builds this map from the same set derives the identical assignment.
 * With more people than palette colors, the used-set resets per cycle (colors
 * repeat only once the palette is exhausted).
 */
export function assignDistinctPersonColors(
  names: (string | null | undefined)[],
): Map<string, string> {
  const sorted = [...new Set(names.filter((n): n is string => !!n))].sort(
    (a, b) => a.localeCompare(b),
  );
  const paletteSize = SPEAKER_COLORS.length;
  let used = new Set<number>();
  const map = new Map<string, string>();
  for (const name of sorted) {
    if (used.size === paletteSize) used = new Set();
    let idx = hashSpeakerLabel(name) % paletteSize;
    while (used.has(idx)) idx = (idx + 1) % paletteSize;
    used.add(idx);
    map.set(name, SPEAKER_COLORS[idx]);
  }
  return map;
}
